import { consoleLogger } from '../ports/logger.js';
import { InvalidPlaybackQueueSnapshotError, } from '../ports/playback-event-store.js';
/** The API accepts at most 100 events per request. */
export const PLAYBACK_EVENT_BATCH_LIMIT = 100;
/** Oldest events are dropped above this, so a long offline stretch can't fill storage. */
export const PLAYBACK_EVENT_CAPACITY = 50_000;
/** A batch that fails this many times in a row is set aside and counted as dropped. */
export const PLAYBACK_BATCH_MAX_ATTEMPTS = 10;
/**
 * crypto.randomUUID only exists from Chrome 92; Samsung/Tizen browsers are older.
 * Falls back to a v4 UUID from getRandomValues, which the server's uuid check accepts.
 */
export function newClientEventId() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function')
        c.getRandomValues(bytes);
    else
        for (let i = 0; i < 16; i += 1)
            bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/**
 * Queue of playback events waiting to be sent. Events are only removed after the server
 * confirms them (ack), so a failed send keeps them for the next sync.
 */
export class PlaybackEventBuffer {
    store;
    options;
    events = [];
    writes = Promise.resolve();
    dirty = false;
    writing = false;
    queueId = newClientEventId();
    droppedEvents = 0;
    acknowledgedDroppedEvents = 0;
    /** False until the stored queue has loaded; nothing is saved before that. */
    loaded = false;
    /** Failures in a row for the batch at the front of the queue. Saved with the queue. */
    headFailure;
    logger;
    constructor(store, options = {}) {
        this.store = store;
        this.options = options;
        this.logger = options.logger ?? consoleLogger;
    }
    /** Restores events saved before a reload. Call once at start. */
    async init() {
        try {
            const saved = await this.store.load();
            const restored = Array.isArray(saved) ? saved : saved.events;
            if (!Array.isArray(saved)) {
                this.queueId = saved.queueId;
                this.droppedEvents = saved.droppedEvents;
                this.acknowledgedDroppedEvents = saved.acknowledgedDroppedEvents;
                const bf = saved.batchFailure;
                if (bf &&
                    typeof bf.firstClientEventId === 'string' &&
                    Number.isSafeInteger(bf.attempts) &&
                    bf.attempts > 0) {
                    this.headFailure = { firstClientEventId: bf.firstClientEventId, attempts: bf.attempts };
                }
            }
            const ids = new Set(this.events.map((e) => e.clientEventId));
            this.events = [...restored.filter((e) => !ids.has(e.clientEventId)), ...this.events];
            this.trim();
            this.loaded = true;
            this.persist();
        }
        catch (error) {
            if (error instanceof InvalidPlaybackQueueSnapshotError) {
                // Unreadable stored queue: retrying won't fix it, and staying unloaded would stop
                // every future save. Start a fresh queue, count what was lost, and save again.
                this.logger.warn('Stored playback queue was invalid, starting a fresh queue', {
                    error: error.message,
                    discardedEvents: error.discardedEvents,
                });
                this.queueId = newClientEventId();
                this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, error.discardedEvents);
                this.acknowledgedDroppedEvents = 0;
                this.headFailure = undefined;
                this.trim();
                this.loaded = true;
                this.persist();
                return;
            }
            this.logger.warn('Playback event queue could not be loaded', {
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }
    record(input) {
        const event = {
            ...input,
            clientEventId: (this.options.newId ?? newClientEventId)(),
        };
        this.events.push(event);
        this.trim();
        this.persist();
        return event;
    }
    /** The oldest events, without removing them. */
    takeBatch(limit = PLAYBACK_EVENT_BATCH_LIMIT) {
        return this.events.slice(0, Math.min(limit, PLAYBACK_EVENT_BATCH_LIMIT));
    }
    /** Removes events the server has confirmed. */
    ack(clientEventIds) {
        if (clientEventIds.length === 0)
            return;
        this.headFailure = undefined;
        const done = new Set(clientEventIds);
        this.events = this.events.filter((e) => !done.has(e.clientEventId));
        this.persist();
    }
    /** Drops everything, e.g. when the device is unpaired (events belong to the old pairing). */
    clear() {
        this.events = [];
        this.queueId = newClientEventId();
        this.droppedEvents = 0;
        this.acknowledgedDroppedEvents = 0;
        this.headFailure = undefined;
        this.persist();
    }
    pendingQueueLoss() {
        if (this.droppedEvents <= this.acknowledgedDroppedEvents)
            return undefined;
        return { queueId: this.queueId, droppedEvents: this.droppedEvents };
    }
    acknowledgeQueueLoss(ack) {
        if (ack.queueId !== this.queueId ||
            !Number.isSafeInteger(ack.droppedEvents) ||
            ack.droppedEvents < 0)
            return;
        this.acknowledgedDroppedEvents = Math.max(this.acknowledgedDroppedEvents, Math.min(ack.droppedEvents, this.droppedEvents));
        this.persist();
    }
    /**
     * Counts a failed send of the batch at the front of the queue. After `limit` failures
     * in a row the batch is set aside: its events leave the queue and count as dropped, so
     * one batch the server can never accept can't block every later event.
     * Returns true when the batch was dropped.
     */
    recordBatchFailure(batch, limit = PLAYBACK_BATCH_MAX_ATTEMPTS) {
        const first = batch[0]?.clientEventId;
        if (!first)
            return false;
        if (this.headFailure?.firstClientEventId === first)
            this.headFailure.attempts += 1;
        else
            this.headFailure = { firstClientEventId: first, attempts: 1 };
        if (this.headFailure.attempts < limit) {
            this.persist(); // so a reload doesn't reset the count
            return false;
        }
        const drop = new Set(batch.map((e) => e.clientEventId));
        const before = this.events.length;
        this.events = this.events.filter((e) => !drop.has(e.clientEventId));
        this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, this.droppedEvents + (before - this.events.length));
        this.headFailure = undefined;
        this.persist();
        return true;
    }
    size() {
        return this.events.length;
    }
    /** Resolves when every write so far has finished (for tests and shutdown). */
    flushWrites() {
        return this.writes;
    }
    trim() {
        const capacity = this.options.capacity ?? PLAYBACK_EVENT_CAPACITY;
        if (!Number.isSafeInteger(capacity) || capacity < 1) {
            throw new Error('Playback queue capacity must be a positive safe integer');
        }
        const excess = this.events.length - capacity;
        if (excess > 0) {
            this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, this.droppedEvents + excess);
            this.events = this.events.slice(excess);
        }
    }
    persist() {
        this.dirty = true;
        // Nothing is saved until the stored queue has loaded, so a failed load
        // can't overwrite it with an empty queue.
        if (!this.loaded || this.writing)
            return;
        this.writing = true;
        this.writes = Promise.resolve().then(async () => {
            try {
                while (this.dirty) {
                    this.dirty = false;
                    // Capture only when a write starts, rather than retaining a full
                    // snapshot for every event recorded while storage is busy.
                    await this.store.save({
                        version: 1,
                        queueId: this.queueId,
                        droppedEvents: this.droppedEvents,
                        acknowledgedDroppedEvents: this.acknowledgedDroppedEvents,
                        ...(this.headFailure ? { batchFailure: { ...this.headFailure } } : {}),
                        events: this.events.map((event) => ({ ...event })),
                    });
                }
            }
            catch (error) {
                this.dirty = true;
                this.logger.warn('Playback event queue could not be saved', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
            finally {
                this.writing = false;
            }
        });
    }
}
//# sourceMappingURL=playback-event-buffer.js.map